import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { fmtDate as sharedFmtDate } from '@/lib/dates';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FileText, Plus, Pencil, RefreshCw, Ban, Trash2, Upload, Download, MapPin,
  CalendarClock, AlertTriangle, ShieldAlert, Clock, X,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  active: { label: 'Active', cls: 'bg-green-100 text-green-700' },
  expiring_soon: { label: 'Expiring Soon', cls: 'bg-amber-100 text-amber-700' },
  expired: { label: 'Expired', cls: 'bg-red-100 text-red-700' },
  open_ended: { label: 'Open-ended', cls: 'bg-sky-100 text-sky-700' },
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-600' },
  terminated: { label: 'Terminated', cls: 'bg-slate-200 text-slate-500' },
  superseded: { label: 'Superseded', cls: 'bg-slate-200 text-slate-500' },
};
const sMeta = (s) => STATUS_META[s] || STATUS_META.draft;
const fmtDate = (d) => (d ? sharedFmtDate(d) : '—');
const daysLabel = (n) => (n === null || n === undefined) ? '' : (n < 0 ? `${Math.abs(n)} days ago` : (n === 0 ? 'Today' : `in ${n} days`));

const EMPTY = {
  name: '', service_category: '', status: 'active', commencement_date: '', expiration_date: '',
  open_ended: false, auto_renew: false, notice_deadline_date: '', renewal_date: '',
  renewal_terms: '', responsible_admin_name: '', internal_notes: '', location_ids: [],
};

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
    {children}
  </label>
);

const ServiceAgreements = ({ customer, locations = [], canManage }) => {
  const { token } = useContext(AuthContext);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const cid = customer.id;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);        // {mode:'add'|'edit', data}
  const [detail, setDetail] = useState(null);     // agreement being viewed
  const [renew, setRenew] = useState(null);       // agreement being renewed
  const [terminate, setTerminate] = useState(null);
  const [docs, setDocs] = useState([]);
  const fileRef = useRef(null);

  const svcLocs = locations.filter((l) => String(l.account_number || '') !== String(customer.account_number || ''));

  const load = () => {
    setLoading(true);
    axios.get(`${API}/customers/${cid}/agreements`, h)
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error('Could not load agreements'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [cid, token]); // eslint-disable-line

  const loadDocs = (aid) => axios.get(`${API}/customer-docs/${cid}?category=service_agreement&agreement_id=${aid}`, h)
    .then((r) => setDocs(r.data || [])).catch(() => setDocs([]));

  const openDetail = (a) => { setDetail(a); loadDocs(a.id); };

  const counts = rows.reduce((acc, a) => {
    acc[a.effective_status] = (acc[a.effective_status] || 0) + 1;
    if (a.needs_date_review) acc.needs_review = (acc.needs_review || 0) + 1;
    return acc;
  }, {});

  // ---- Save (create / update) ----
  const save = async () => {
    const d = form.data;
    if (!d.name.trim()) { toast.error('Agreement name is required'); return; }
    const payload = {
      name: d.name, service_category: d.service_category || null, status: d.status,
      commencement_date: d.commencement_date || null, expiration_date: d.open_ended ? null : (d.expiration_date || null),
      open_ended: d.open_ended, auto_renew: d.auto_renew,
      notice_deadline_date: d.auto_renew ? (d.notice_deadline_date || null) : null,
      renewal_date: d.renewal_date || null, renewal_terms: d.renewal_terms || null,
      responsible_admin_name: d.responsible_admin_name || null, internal_notes: d.internal_notes || null,
      location_ids: d.location_ids || [],
    };
    try {
      if (form.mode === 'add') {
        await axios.post(`${API}/customers/${cid}/agreements`, payload, h);
        toast.success('Agreement created');
      } else {
        await axios.put(`${API}/service-agreements/${form.data.id}`, payload, h);
        toast.success('Agreement updated');
      }
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const doRenew = async () => {
    const d = renew;
    if (!d.new_expiration_date) { toast.error('New expiration date is required'); return; }
    try {
      await axios.post(`${API}/service-agreements/${d.id}/renew`, {
        new_expiration_date: d.new_expiration_date, new_commencement_date: d.new_commencement_date || null,
        renewal_terms: d.renewal_terms || null, note: d.note || null,
        auto_renew: d.auto_renew, notice_deadline_date: d.notice_deadline_date || null,
      }, h);
      toast.success('Agreement renewed'); setRenew(null); setDetail(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Renew failed'); }
  };

  const doTerminate = async () => {
    if (!terminate.termination_date) { toast.error('Termination date is required'); return; }
    try {
      await axios.post(`${API}/service-agreements/${terminate.id}/terminate`, {
        termination_date: terminate.termination_date, note: terminate.note || null,
      }, h);
      toast.success('Agreement terminated'); setTerminate(null); setDetail(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Terminate failed'); }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0]; if (!file || !detail) return;
    const b64 = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(file); });
    try {
      await axios.post(`${API}/customer-docs/${cid}/upload`, {
        filename: file.name, content_type: file.type || 'application/octet-stream',
        data_base64: b64, category: 'service_agreement', agreement_id: detail.id,
      }, h);
      toast.success('Document uploaded'); loadDocs(detail.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    e.target.value = '';
  };
  const delDoc = async (docId) => {
    try { await axios.delete(`${API}/customer-docs/${cid}/${docId}`, h); toast.success('Document deleted'); loadDocs(detail.id); }
    catch { toast.error('Delete failed'); }
  };
  const docUrl = (d) => `${BACKEND_URL}/api/customer-docs/${cid}/${d.id}/file?auth=${token}`;

  const setF = (patch) => setForm((f) => ({ ...f, data: { ...f.data, ...patch } }));
  const toggleLoc = (id) => setF({ location_ids: form.data.location_ids.includes(id) ? form.data.location_ids.filter((x) => x !== id) : [...form.data.location_ids, id] });

  return (
    <div className="space-y-4" data-testid="agreements-panel">
      {/* Summary + add */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="agreements-summary">
          <span className="rounded-full bg-green-100 px-2.5 py-1 font-semibold text-green-700">{counts.active || 0} Active</span>
          <span className="rounded-full bg-amber-100 px-2.5 py-1 font-semibold text-amber-700">{counts.expiring_soon || 0} Expiring ≤45d</span>
          <span className="rounded-full bg-red-100 px-2.5 py-1 font-semibold text-red-700">{counts.expired || 0} Expired</span>
          {(counts.needs_review || 0) > 0 && <span className="rounded-full bg-orange-100 px-2.5 py-1 font-semibold text-orange-700">{counts.needs_review} Need date review</span>}
        </div>
        {canManage && (
          <Button onClick={() => setForm({ mode: 'add', data: { ...EMPTY } })} data-testid="agreement-add-btn"
            className="bg-[#0E2A47] hover:bg-[#16324F]"><Plus size={15} className="mr-1.5" /> Add Agreement</Button>
        )}
      </div>

      {loading ? <p className="text-sm text-slate-400">Loading agreements…</p> : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-slate-400" data-testid="agreements-empty">
          <FileText size={26} className="mx-auto mb-2 text-slate-300" />
          No service agreements yet.{canManage ? ' Use “Add Agreement” to create one.' : ''}
        </div>
      ) : (
        <div className="grid gap-2" data-testid="agreements-list">
          {rows.map((a) => {
            const m = sMeta(a.effective_status);
            const locNames = (a.location_ids || []).map((id) => locations.find((l) => l.id === id)?.location_name).filter(Boolean);
            return (
              <button key={a.id} onClick={() => openDetail(a)} data-testid={`agreement-card-${a.id}`}
                className="w-full min-w-0 rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:border-[#8FC93A] hover:shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] font-semibold text-slate-400">{a.agreement_number}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.cls}`} data-testid={`agreement-status-${a.id}`}>{m.label}</span>
                      {a.auto_renew && <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">Auto-renew</span>}
                      {a.needs_date_review && <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700"><AlertTriangle size={10} /> Date review</span>}
                    </div>
                    <div className="mt-0.5 truncate font-semibold text-[#0E2A47]">{a.name}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                      {a.service_category && <span>{a.service_category}</span>}
                      <span className="inline-flex items-center gap-1"><CalendarClock size={11} />
                        {a.open_ended ? 'Open-ended' : <>Expires {fmtDate(a.expiration_date)}</>}
                      </span>
                      {locNames.length > 0 && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {locNames.length} location{locNames.length > 1 ? 's' : ''}</span>}
                    </div>
                  </div>
                  {!a.open_ended && a.days_until_expiration !== null && ['active', 'expiring_soon', 'expired'].includes(a.effective_status) && (
                    <div className={`shrink-0 text-right text-xs font-semibold ${a.effective_status === 'expired' ? 'text-red-600' : a.effective_status === 'expiring_soon' ? 'text-amber-600' : 'text-slate-400'}`}>
                      {daysLabel(a.days_until_expiration)}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ---- Add / Edit form ---- */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="agreement-form">
          <DialogHeader><DialogTitle>{form?.mode === 'add' ? 'New Service Agreement' : 'Edit Service Agreement'}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2"><Field label="Agreement Name *"><Input value={form.data.name} onChange={(e) => setF({ name: e.target.value })} data-testid="agreement-name" placeholder="e.g. Janitorial Master Services Agreement" /></Field></div>
                <Field label="Service Category"><Input value={form.data.service_category} onChange={(e) => setF({ service_category: e.target.value })} data-testid="agreement-category" placeholder="e.g. Janitorial" /></Field>
                <Field label="Status">
                  <select value={form.data.status} onChange={(e) => setF({ status: e.target.value })} data-testid="agreement-status-select" className="min-h-10 rounded-md border border-slate-300 px-2 text-sm">
                    <option value="draft">Draft</option><option value="active">Active</option>
                    <option value="terminated">Terminated</option><option value="superseded">Superseded</option>
                  </select>
                </Field>
                <Field label="Commencement Date"><Input type="date" value={form.data.commencement_date || ''} onChange={(e) => setF({ commencement_date: e.target.value })} data-testid="agreement-commencement" /></Field>
                <Field label="Expiration / Maturity Date">
                  <Input type="date" value={form.data.expiration_date || ''} disabled={form.data.open_ended} onChange={(e) => setF({ expiration_date: e.target.value })} data-testid="agreement-expiration" />
                </Field>
                <label className="flex items-center gap-2 text-sm text-slate-600 sm:col-span-2" data-testid="agreement-openended-row">
                  <Checkbox checked={form.data.open_ended} onCheckedChange={(v) => setF({ open_ended: !!v, expiration_date: v ? '' : form.data.expiration_date })} data-testid="agreement-openended" />
                  This agreement is explicitly open-ended (no expiration date)
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600 sm:col-span-2">
                  <Checkbox checked={form.data.auto_renew} onCheckedChange={(v) => setF({ auto_renew: !!v })} data-testid="agreement-autorenew" />
                  Automatically renews (requires a notice deadline)
                </label>
                {form.data.auto_renew && (
                  <Field label="Notice Deadline Date"><Input type="date" value={form.data.notice_deadline_date || ''} onChange={(e) => setF({ notice_deadline_date: e.target.value })} data-testid="agreement-notice" /></Field>
                )}
                <Field label="Renewal Date"><Input type="date" value={form.data.renewal_date || ''} onChange={(e) => setF({ renewal_date: e.target.value })} data-testid="agreement-renewal-date" /></Field>
                <Field label="Responsible Administrator"><Input value={form.data.responsible_admin_name} onChange={(e) => setF({ responsible_admin_name: e.target.value })} data-testid="agreement-responsible" placeholder="Account manager" /></Field>
                <div className="sm:col-span-2"><Field label="Renewal Terms"><Textarea value={form.data.renewal_terms} onChange={(e) => setF({ renewal_terms: e.target.value })} data-testid="agreement-renewal-terms" rows={2} /></Field></div>
                <div className="sm:col-span-2"><Field label="Internal Notes"><Textarea value={form.data.internal_notes} onChange={(e) => setF({ internal_notes: e.target.value })} data-testid="agreement-notes" rows={2} /></Field></div>
              </div>
              {svcLocs.length > 0 && (
                <div>
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Covered Service Locations</div>
                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2" data-testid="agreement-locations">
                    {svcLocs.map((l) => (
                      <label key={l.id} className="flex items-center gap-2 text-sm text-slate-700" data-testid={`agreement-loc-${l.id}`}>
                        <Checkbox checked={form.data.location_ids.includes(l.id)} onCheckedChange={() => toggleLoc(l.id)} />
                        <span className="truncate">{l.location_name} <span className="font-mono text-xs text-slate-400">#{l.account_number || '—'}</span></span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} data-testid="agreement-cancel">Cancel</Button>
            <Button onClick={save} data-testid="agreement-save" className="bg-[#0E2A47] hover:bg-[#16324F]">{form?.mode === 'add' ? 'Create Agreement' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Detail ---- */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="agreement-detail">
          {detail && (() => {
            const m = sMeta(detail.effective_status);
            const locNames = (detail.location_ids || []).map((id) => locations.find((l) => l.id === id)?.location_name || 'Location');
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-slate-400">{detail.agreement_number}</span>
                    {detail.name}
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.cls}`}>{m.label}</span>
                  </DialogTitle>
                </DialogHeader>
                {detail.needs_date_review && (
                  <div className="flex items-center gap-2 rounded-md bg-orange-50 px-3 py-2 text-xs font-medium text-orange-700" data-testid="agreement-review-flag">
                    <AlertTriangle size={14} /> This active agreement has no confirmed expiration date — mark it open-ended or add a maturity date.
                  </div>
                )}
                {detail.effective_status === 'expiring_soon' && (
                  <div className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700" data-testid="agreement-expiry-flag">
                    <Clock size={14} /> Expires {fmtDate(detail.expiration_date)} ({daysLabel(detail.days_until_expiration)}).
                  </div>
                )}
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-slate-200 bg-white p-4 text-sm sm:grid-cols-3">
                  <DRow label="Category" value={detail.service_category} />
                  <DRow label="Commencement" value={fmtDate(detail.commencement_date)} />
                  <DRow label="Expiration" value={detail.open_ended ? 'Open-ended' : fmtDate(detail.expiration_date)} />
                  <DRow label="Auto-renew" value={detail.auto_renew ? 'Yes' : 'No'} />
                  {detail.auto_renew && <DRow label="Notice Deadline" value={fmtDate(detail.notice_deadline_date)} />}
                  <DRow label="Renewal Date" value={fmtDate(detail.renewal_date)} />
                  <DRow label="Termination" value={fmtDate(detail.termination_date)} />
                  <DRow label="Responsible" value={detail.responsible_admin_name} />
                </div>
                {locNames.length > 0 && (
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Covered Locations ({locNames.length})</div>
                    <div className="flex flex-wrap gap-1.5">{locNames.map((n, i) => <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{n}</span>)}</div>
                  </div>
                )}
                {detail.renewal_terms && <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Renewal Terms</div><p className="mt-1 text-slate-700">{detail.renewal_terms}</p></div>}
                {detail.internal_notes && <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Internal Notes</div><p className="mt-1 text-slate-700">{detail.internal_notes}</p></div>}

                {/* Documents */}
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Agreement Documents</div>
                    {canManage && <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="agreement-doc-upload"><Upload size={13} className="mr-1" /> Upload</Button>}
                  </div>
                  <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} data-testid="agreement-doc-input" />
                  {docs.length === 0 ? <p className="text-sm text-slate-400">No documents uploaded.</p> : (
                    <div className="space-y-2">
                      {docs.map((d) => (
                        <div key={d.id} className="flex items-center justify-between gap-3 rounded-md border border-slate-100 p-2" data-testid={`agreement-doc-${d.id}`}>
                          <a href={docUrl(d)} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-2 text-sm font-medium text-[#0E2A47] hover:underline"><FileText size={15} /> <span className="truncate">{d.title || d.filename}</span></a>
                          <div className="flex items-center gap-3 text-xs text-slate-400">
                            <a href={docUrl(d)} target="_blank" rel="noreferrer" className="hover:text-[#0E2A47]"><Download size={14} /></a>
                            {canManage && <button onClick={() => delDoc(d.id)} className="text-red-500 hover:text-red-700" data-testid={`agreement-doc-del-${d.id}`}><Trash2 size={14} /></button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Renewal history */}
                {(detail.renewal_history || []).length > 0 && (
                  <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="agreement-renewal-history">
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Renewal History</div>
                    <div className="space-y-2">
                      {detail.renewal_history.map((rh, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-slate-600">
                          <RefreshCw size={12} className="mt-0.5 text-slate-400" />
                          <div>Renewed {rh.previous_expiration_date ? fmtDate(rh.previous_expiration_date) : '—'} → <span className="font-semibold">{fmtDate(rh.new_expiration_date)}</span>{rh.renewed_by_name ? ` · by ${rh.renewed_by_name}` : ''}{rh.note ? ` · ${rh.note}` : ''}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {canManage && (
                  <DialogFooter className="flex-wrap gap-2">
                    {detail.status !== 'terminated' && (
                      <Button variant="outline" onClick={() => setTerminate({ id: detail.id, termination_date: '', note: '' })} data-testid="agreement-terminate-btn" className="text-red-600 hover:text-red-700"><Ban size={14} className="mr-1" /> Terminate</Button>
                    )}
                    {detail.status === 'active' && (
                      <Button variant="outline" onClick={() => setRenew({ id: detail.id, new_expiration_date: '', new_commencement_date: '', renewal_terms: detail.renewal_terms || '', auto_renew: detail.auto_renew, notice_deadline_date: detail.notice_deadline_date || '', note: '' })} data-testid="agreement-renew-btn"><RefreshCw size={14} className="mr-1" /> Renew</Button>
                    )}
                    <Button onClick={() => { setForm({ mode: 'edit', data: { ...EMPTY, ...detail, location_ids: detail.location_ids || [] } }); setDetail(null); }} data-testid="agreement-edit-btn" className="bg-[#0E2A47] hover:bg-[#16324F]"><Pencil size={14} className="mr-1" /> Edit</Button>
                  </DialogFooter>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ---- Renew ---- */}
      <Dialog open={!!renew} onOpenChange={(o) => !o && setRenew(null)}>
        <DialogContent className="max-w-lg" data-testid="agreement-renew-dialog">
          <DialogHeader><DialogTitle>Renew Agreement</DialogTitle></DialogHeader>
          {renew && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="New Expiration Date *"><Input type="date" value={renew.new_expiration_date} onChange={(e) => setRenew({ ...renew, new_expiration_date: e.target.value })} data-testid="renew-expiration" /></Field>
              <Field label="New Commencement (optional)"><Input type="date" value={renew.new_commencement_date} onChange={(e) => setRenew({ ...renew, new_commencement_date: e.target.value })} data-testid="renew-commencement" /></Field>
              <label className="flex items-center gap-2 text-sm text-slate-600 sm:col-span-2">
                <Checkbox checked={renew.auto_renew} onCheckedChange={(v) => setRenew({ ...renew, auto_renew: !!v })} /> Automatically renews
              </label>
              {renew.auto_renew && <Field label="Notice Deadline"><Input type="date" value={renew.notice_deadline_date} onChange={(e) => setRenew({ ...renew, notice_deadline_date: e.target.value })} data-testid="renew-notice" /></Field>}
              <div className="sm:col-span-2"><Field label="Note"><Textarea rows={2} value={renew.note} onChange={(e) => setRenew({ ...renew, note: e.target.value })} data-testid="renew-note" /></Field></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenew(null)}>Cancel</Button>
            <Button onClick={doRenew} data-testid="renew-confirm" className="bg-[#0E2A47] hover:bg-[#16324F]">Confirm Renewal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Terminate ---- */}
      <Dialog open={!!terminate} onOpenChange={(o) => !o && setTerminate(null)}>
        <DialogContent className="max-w-lg" data-testid="agreement-terminate-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><ShieldAlert size={18} /> Terminate Agreement</DialogTitle></DialogHeader>
          {terminate && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Record the termination date. History and documents are preserved.</p>
              <Field label="Termination Date *"><Input type="date" value={terminate.termination_date} onChange={(e) => setTerminate({ ...terminate, termination_date: e.target.value })} data-testid="terminate-date" /></Field>
              <Field label="Note"><Textarea rows={2} value={terminate.note} onChange={(e) => setTerminate({ ...terminate, note: e.target.value })} data-testid="terminate-note" /></Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTerminate(null)}>Cancel</Button>
            <Button onClick={doTerminate} data-testid="terminate-confirm" className="bg-red-600 hover:bg-red-700">Terminate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DRow = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-sm text-[#0E2A47]">{value || '—'}</span>
  </div>
);

export default ServiceAgreements;
